import styles from './TelemetrySettings.module.css'

export type TelemetryFieldId =
  | 'time.clock' | 'time.tps' | 'time.ping' | 'time.gamemode'
  | 'world.coords' | 'world.yaw' | 'world.pitch' | 'world.chunk'
  | 'world.light' | 'world.biome' | 'world.speed'
  | 'panels.nearby' | 'panels.packets' | 'panels.metrics'

interface Group {
  label: string
  fields: Array<{ id: TelemetryFieldId; label: string }>
}

export const TELEMETRY_GROUPS: Group[] = [
  {
    label: 'Время и состояние',
    fields: [
      { id: 'time.clock', label: 'Время и дата' },
      { id: 'time.tps', label: 'TPS' },
      { id: 'time.ping', label: 'Пинг' },
      { id: 'time.gamemode', label: 'Режим игры' },
    ],
  },
  {
    label: 'Мир и позиция',
    fields: [
      { id: 'world.coords', label: 'Координаты XYZ' },
      { id: 'world.yaw', label: 'Yaw' },
      { id: 'world.pitch', label: 'Pitch' },
      { id: 'world.chunk', label: 'Чанк X, Z' },
      { id: 'world.light', label: 'Освещение' },
      { id: 'world.biome', label: 'Биом и движение' },
      { id: 'world.speed', label: 'Скорость' },
    ],
  },
  {
    label: 'Дополнительные блоки',
    fields: [
      { id: 'panels.nearby', label: 'Игроки поблизости' },
      { id: 'panels.packets', label: 'Пакеты' },
      { id: 'panels.metrics', label: 'Память и FPS' },
    ],
  },
]

export const ALL_TELEMETRY_FIELDS = TELEMETRY_GROUPS.flatMap(g => g.fields.map(f => f.id))

const CheckMark = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={styles.mark} aria-hidden="true">
    <path d="M15.0322 6.2197C15.3251 5.9268 15.7998 5.9268 16.0927 6.2197C16.3856 6.5126 16.3856 6.9874 16.0927 7.2803L8.5927 14.7803C8.2998 15.0731 7.8251 15.0731 7.5322 14.7803L4.4072 11.6553C4.1143 11.3624 4.1143 10.8876 4.4072 10.5947C4.7001 10.3018 5.1748 10.3018 5.4677 10.5947L8.0624 13.1894L15.0322 6.2197Z" />
  </svg>
)

const DashMark = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className={styles.mark} aria-hidden="true">
    <path d="M16 9.25C16.4142 9.25 16.75 9.586 16.75 10C16.75 10.414 16.4142 10.75 16 10.75H4C3.5858 10.75 3.25 10.414 3.25 10C3.25 9.586 3.5858 9.25 4 9.25H16Z" />
  </svg>
)

function Row ({ label, state, child, onToggle }: {
  label: string
  state: 'on' | 'off' | 'mixed'
  child?: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={state === 'mixed' ? 'mixed' : state === 'on'}
      className={`${styles.row}${child ? ` ${styles.rowChild}` : ''}`}
      onClick={onToggle}
    >
      <span className={`${styles.box}${state === 'on' ? ` ${styles.boxOn}` : state === 'mixed' ? ` ${styles.boxMixed}` : ''}`}>
        {state === 'on' && <CheckMark />}
        {state === 'mixed' && <DashMark />}
      </span>
      <span className={styles.label}>{label}</span>
    </button>
  )
}

interface Props {
  title?: string
  /** Rendered inside the telemetry panel — drops its own surface and title. */
  embedded?: boolean
  hidden: Set<string>
  onChange: (nextHidden: Set<string>) => void
}

export default function TelemetrySettings ({ title = 'Настройки телеметрии', embedded = false, hidden, onChange }: Props) {
  const toggleField = (id: TelemetryFieldId) => {
    const next = new Set(hidden)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onChange(next)
  }

  const toggleGroup = (group: Group) => {
    const next = new Set(hidden)
    const allOn = group.fields.every(f => !next.has(f.id))
    for (const f of group.fields) {
      if (allOn) next.add(f.id)
      else next.delete(f.id)
    }
    onChange(next)
  }

  return (
    <div className={embedded ? styles.embedded : styles.panel} data-replay-ui>
      {!embedded && <div className={styles.title}>{title}</div>}

      {TELEMETRY_GROUPS.map(group => {
        const on = group.fields.filter(f => !hidden.has(f.id)).length
        const groupState = on === 0 ? 'off' : on === group.fields.length ? 'on' : 'mixed'
        return (
          <div key={group.label} className={styles.group}>
            <Row
              label={group.label}
              state={groupState}
              onToggle={() => toggleGroup(group)}
            />
            {group.fields.map(f => (
              <Row
                key={f.id}
                child
                label={f.label}
                state={hidden.has(f.id) ? 'off' : 'on'}
                onToggle={() => toggleField(f.id)}
              />
            ))}
          </div>
        )
      })}
    </div>
  )
}
